/**
 * SocialMediaComposer Component
 * Compose and send posts to connected social media platforms
 */

import React, { useState, useEffect } from 'react';
import {
  Send,
  Facebook,
  Instagram,
  Twitter,
  Linkedin,
  Cloud,
  AlertCircle,
  CheckCircle,
  Image,
  Link as LinkIcon,
  Hash,
  Calendar,
  Clock,
  Paperclip,
  X,
  Share2
} from 'lucide-react';
import { getWeaveinApiUrl } from '@/src/config';
import { MediaLibrarySelector } from './MediaLibrarySelector';

import { useAppTranslation } from '@/contexts/TranslationContext';
import { authFetch } from '@/lib/authFetch';

// Social platform configuration
const SOCIAL_PLATFORMS = [
  { code: 'facebook', name: 'Facebook', icon: Facebook, color: 'bg-blue-600', maxLength: 63206 },
  { code: 'instagram', name: 'Instagram', icon: Instagram, color: 'bg-gradient-to-r from-purple-600 to-pink-500', maxLength: 2200 },
  { code: 'twitter', name: 'X (Twitter)', icon: Twitter, color: 'bg-black', maxLength: 280 },
  { code: 'linkedin', name: 'LinkedIn', icon: Linkedin, color: 'bg-blue-700', maxLength: 3000 },
  { code: 'bluesky', name: 'Bluesky', icon: Cloud, color: 'bg-sky-500', maxLength: 300 }
];

interface SocialConnection {
  id: string;
  platform_code: string;
  platform_name: string;
  account_name: string;
  connection_status: string;
}

interface MediaAttachment {
  id: number;
  uuid: string;
  name: string;
  asset_type: string;
  thumbnail_url?: string;
  short_url?: string;
}

interface SocialMediaComposerProps {
  onSendSuccess?: (result: any) => void;
  onSendError?: (error: Error) => void;
}

export const SocialMediaComposer: React.FC<SocialMediaComposerProps> = ({
  onSendSuccess,
  onSendError,
}) => {
  const { t } = useAppTranslation();

  // State
  const [content, setContent] = useState('');
  const [hashtags, setHashtags] = useState('');
  const [selectedPlatforms, setSelectedPlatforms] = useState<string[]>([]);
  const [socialConnections, setSocialConnections] = useState<SocialConnection[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingConnections, setLoadingConnections] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Media attachments
  const [showMediaSelector, setShowMediaSelector] = useState(false);
  const [selectedMedia, setSelectedMedia] = useState<MediaAttachment[]>([]);

  // Schedule options
  const [schedulePost, setSchedulePost] = useState(false);
  const [scheduledDate, setScheduledDate] = useState('');
  const [scheduledTime, setScheduledTime] = useState('');

  // Load social connections on mount
  useEffect(() => {
    loadSocialConnections();
  }, []);

  const loadSocialConnections = async () => {
    setLoadingConnections(true);
    try {
      // Get organization ID from user context or localStorage
      const userStr = localStorage.getItem('user');
      const user = userStr ? JSON.parse(userStr) : null;
      const organizationId = user?.organization_id || user?.cbo_id;

      if (!organizationId) {
        setSocialConnections([]);
        setLoadingConnections(false);
        return;
      }

      const response = await authFetch(`${getWeaveinApiUrl()}/api/social-media/connections?organization_id=${organizationId}`);

      if (response.ok) {
        const connections = await response.json();
        setSocialConnections(connections.filter((c: SocialConnection) => c.connection_status === 'active'));
      }
    } catch (error) {
      console.error('Failed to load social connections:', error);
    } finally {
      setLoadingConnections(false);
    }
  };

  const isPlatformConnected = (platformCode: string) => {
    return socialConnections.some(c => c.platform_code === platformCode);
  };

  const getConnectionForPlatform = (platformCode: string) => {
    return socialConnections.find(c => c.platform_code === platformCode);
  };

  const togglePlatform = (platformCode: string) => {
    if (selectedPlatforms.includes(platformCode)) {
      setSelectedPlatforms(selectedPlatforms.filter(p => p !== platformCode));
    } else {
      setSelectedPlatforms([...selectedPlatforms, platformCode]);
    }
  };

  const getCharacterLimit = () => {
    // Return the smallest limit among selected platforms
    if (selectedPlatforms.length === 0) return 280;
    const limits = selectedPlatforms.map(p => {
      const platform = SOCIAL_PLATFORMS.find(sp => sp.code === p);
      return platform?.maxLength || 280;
    });
    return Math.min(...limits);
  };

  const getFullContent = () => {
    let fullContent = content;
    if (hashtags.trim()) {
      fullContent = `${content}\n\n${hashtags}`;
    }
    return fullContent;
  };

  const handleMediaSelect = (media: MediaAttachment) => {
    if (selectedMedia.find(m => m.id === media.id)) {
      setSelectedMedia(selectedMedia.filter(m => m.id !== media.id));
    } else {
      // Limit to 4 media items (common limit across platforms)
      if (selectedMedia.length < 4) {
        setSelectedMedia([...selectedMedia, media]);
      }
    }
  };

  const removeMedia = (mediaId: number) => {
    setSelectedMedia(selectedMedia.filter(m => m.id !== mediaId));
  };

  const handleSend = async () => {
    setError(null);
    setSuccess(null);

    // Validation
    if (selectedPlatforms.length === 0) {
      setError('Please select at least one platform to post to');
      return;
    }

    if (!content.trim()) {
      setError('Please enter content for your post');
      return;
    }

    const fullContent = getFullContent();
    const charLimit = getCharacterLimit();
    if (fullContent.length > charLimit) {
      setError(`Content exceeds character limit for selected platforms (${charLimit} characters)`);
      return;
    }

    setLoading(true);

    try {
      const userStr = localStorage.getItem('user');
      const user = userStr ? JSON.parse(userStr) : null;
      const organizationId = user?.organization_id || user?.cbo_id;

      // Prepare media URLs if any
      const mediaUrls = selectedMedia.map(m => m.short_url || m.thumbnail_url).filter(Boolean);

      const requestBody = {
        organization_id: organizationId,
        platforms: selectedPlatforms,
        content: content,
        hashtags: hashtags || undefined,
        media_urls: mediaUrls.length > 0 ? mediaUrls : undefined,
        scheduled_at: schedulePost && scheduledDate && scheduledTime
          ? new Date(`${scheduledDate}T${scheduledTime}`).toISOString()
          : undefined
      };

      const response = await authFetch(`${getWeaveinApiUrl()}/api/bulk-communications/send-social`, {
        method: 'POST',
        jsonBody: requestBody
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to post to social media');
      }

      const result = await response.json();

      setSuccess(schedulePost
        ? `Post scheduled successfully for ${selectedPlatforms.length} platform(s)!`
        : `Post sent successfully to ${selectedPlatforms.length} platform(s)!`
      );

      // Clear form
      setContent('');
      setHashtags('');
      setSelectedPlatforms([]);
      setSelectedMedia([]);
      setSchedulePost(false);
      setScheduledDate('');
      setScheduledTime('');

      if (onSendSuccess) {
        onSendSuccess(result);
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to post to social media';
      setError(errorMessage);
      if (onSendError) {
        onSendError(err instanceof Error ? err : new Error(errorMessage));
      }
    } finally {
      setLoading(false);
    }
  };

  const charLimit = getCharacterLimit();
  const fullContentLength = getFullContent().length;

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 shadow-sm">
      <div className="p-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
          <Share2 className="w-5 h-5 text-purple-600" />
          {t('communications.post_to_social_media', 'Post to Social Media')}
        </h3>

        {/* Alerts */}
        {error && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm text-red-800">{error}</p>
            </div>
            <button onClick={() => setError(null)} className="text-red-600 hover:text-red-800">
              ×
            </button>
          </div>
        )}

        {success && (
          <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg flex items-start gap-3">
            <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm text-green-800">{success}</p>
            </div>
            <button onClick={() => setSuccess(null)} className="text-green-600 hover:text-green-800">
              ×
            </button>
          </div>
        )}

        {/* Platform Selection */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            {t('communications.select_platforms', 'Select Platforms *')}
          </label>

          {loadingConnections ? (
            <div className="text-sm text-gray-500">Loading connected platforms...</div>
          ) : socialConnections.length === 0 ? (
            <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-lg p-4">
              <p className="text-sm text-amber-700 dark:text-amber-400">
                No social media platforms connected.
                <a href="/settings" className="underline ml-1 font-medium">Connect platforms in Settings</a>
              </p>
            </div>
          ) : (
            <div className="flex flex-wrap gap-2">
              {SOCIAL_PLATFORMS.map((platform) => {
                const isConnected = isPlatformConnected(platform.code);
                const isSelected = selectedPlatforms.includes(platform.code);
                const connection = getConnectionForPlatform(platform.code);
                const Icon = platform.icon;

                return (
                  <button
                    key={platform.code}
                    type="button"
                    onClick={() => isConnected && togglePlatform(platform.code)}
                    disabled={!isConnected}
                    className={`flex items-center gap-2 px-4 py-2.5 rounded-lg border-2 transition-all ${
                      !isConnected
                        ? 'border-gray-200 text-gray-400 cursor-not-allowed bg-gray-50 opacity-60'
                        : isSelected
                        ? `${platform.color} border-transparent text-white shadow-md`
                        : 'border-gray-300 text-gray-700 hover:border-gray-400 hover:bg-gray-50'
                    }`}
                    title={isConnected ? `${platform.name}: ${connection?.account_name}` : `${platform.name} not connected`}
                  >
                    <Icon className="w-5 h-5" />
                    <span className="font-medium">{platform.name}</span>
                    {isConnected && !isSelected && (
                      <CheckCircle className="w-4 h-4 text-green-500" />
                    )}
                  </button>
                );
              })}
            </div>
          )}

          {selectedPlatforms.length > 0 && (
            <p className="text-xs text-gray-500 mt-2">
              Character limit: {charLimit} (based on smallest platform limit)
            </p>
          )}
        </div>

        {/* Content Input */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            {t('communications.post_content', 'Post Content *')}
          </label>
          <textarea
            value={content}
            onChange={(e) => setContent(e.target.value)}
            disabled={loading}
            rows={5}
            placeholder="What do you want to share with your audience?"
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent disabled:bg-gray-100 dark:bg-gray-700 dark:text-white"
          />
          <div className="flex justify-between items-center mt-1">
            <p className="text-xs text-gray-500">
              Supports: {'{organization_name}'} placeholder
            </p>
            <p className={`text-xs ${fullContentLength > charLimit ? 'text-red-600 font-medium' : 'text-gray-500'}`}>
              {fullContentLength}/{charLimit} characters
            </p>
          </div>
        </div>

        {/* Hashtags */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 flex items-center gap-1">
            <Hash className="w-4 h-4" />
            {t('communications.hashtags', 'Hashtags')}
          </label>
          <input
            type="text"
            value={hashtags}
            onChange={(e) => setHashtags(e.target.value)}
            disabled={loading}
            placeholder="#HealthAwareness #CommunityHealth #WellBeing"
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent disabled:bg-gray-100 dark:bg-gray-700 dark:text-white"
          />
          <p className="text-xs text-gray-500 mt-1">
            Separate hashtags with spaces. These will be appended to your post.
          </p>
        </div>

        {/* Media Attachments */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 flex items-center gap-1">
            <Paperclip className="w-4 h-4" />
            {t('communications.media_attachments', 'Media Attachments')}
          </label>

          {selectedMedia.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-3">
              {selectedMedia.map((media) => (
                <div
                  key={media.id}
                  className="relative group bg-gray-100 dark:bg-gray-700 rounded-lg p-2 pr-8 flex items-center gap-2"
                >
                  {media.thumbnail_url ? (
                    <img src={media.thumbnail_url} alt={media.name} className="w-8 h-8 rounded object-cover" />
                  ) : (
                    <div className="w-8 h-8 bg-gray-200 dark:bg-gray-600 rounded flex items-center justify-center">
                      <Image className="w-4 h-4 text-gray-400" />
                    </div>
                  )}
                  <span className="text-sm text-gray-700 dark:text-gray-300 truncate max-w-[150px]">
                    {media.name}
                  </span>
                  <button
                    onClick={() => removeMedia(media.id)}
                    className="absolute right-1 top-1/2 -translate-y-1/2 p-1 text-gray-400 hover:text-red-500"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          )}

          <button
            type="button"
            onClick={() => setShowMediaSelector(true)}
            disabled={selectedMedia.length >= 4}
            className="flex items-center gap-2 px-4 py-2 border border-dashed border-gray-300 dark:border-gray-600 rounded-lg text-gray-600 dark:text-gray-400 hover:border-purple-500 hover:text-purple-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Image className="w-4 h-4" />
            {selectedMedia.length > 0 ? 'Add More Media' : 'Add Media from Library'}
          </button>
          <p className="text-xs text-gray-500 mt-1">
            {selectedMedia.length}/4 media items. Images and videos from your media library.
          </p>
        </div>

        {/* Schedule Options */}
        <div className="mb-6 p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={schedulePost}
              onChange={(e) => setSchedulePost(e.target.checked)}
              disabled={loading}
              className="w-4 h-4 text-purple-600 rounded focus:ring-2 focus:ring-purple-500"
            />
            <Calendar className="w-4 h-4 text-gray-600 dark:text-gray-400" />
            <span className="text-sm text-gray-700 dark:text-gray-300">
              {t('communications.schedule_for_later', 'Schedule for later')}
            </span>
          </label>

          {schedulePost && (
            <div className="mt-3 grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs text-gray-500 mb-1">Date</label>
                <input
                  type="date"
                  value={scheduledDate}
                  onChange={(e) => setScheduledDate(e.target.value)}
                  min={new Date().toISOString().split('T')[0]}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm dark:bg-gray-700 dark:text-white"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Time</label>
                <input
                  type="time"
                  value={scheduledTime}
                  onChange={(e) => setScheduledTime(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm dark:bg-gray-700 dark:text-white"
                />
              </div>
            </div>
          )}
        </div>

        {/* Send Button */}
        <button
          onClick={handleSend}
          disabled={loading || selectedPlatforms.length === 0 || !content.trim()}
          className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-purple-600 to-pink-500 text-white rounded-lg hover:from-purple-700 hover:to-pink-600 transition-all disabled:opacity-50 disabled:cursor-not-allowed font-medium shadow-md"
        >
          {loading ? (
            <>
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
              {schedulePost ? 'Scheduling...' : 'Posting...'}
            </>
          ) : (
            <>
              {schedulePost ? <Clock className="w-5 h-5" /> : <Send className="w-5 h-5" />}
              {schedulePost ? 'Schedule Post' : `Post to ${selectedPlatforms.length || 0} Platform${selectedPlatforms.length !== 1 ? 's' : ''}`}
            </>
          )}
        </button>
      </div>

      {/* Media Library Selector Modal */}
      {showMediaSelector && (
        <MediaLibrarySelector
          onSelect={handleMediaSelect}
          onClose={() => setShowMediaSelector(false)}
          selectedIds={selectedMedia.map(m => m.id)}
          maxSelection={4 - selectedMedia.length}
        />
      )}
    </div>
  );
};
